import { useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createViewDealCreate } from "@/modules/rev_eu/partners/views/ViewDealCreate";
import { createViewDeals } from "@/modules/rev_eu/partners/views/ViewDeals";
import { routerPaths } from "@/ui/router";
import { NOOP } from "@/utils/utils";
import type { TApiDeal } from "@/apis/proda_finance";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { TAsyncState, TDeal } from "@/store";
import { buildDealCopyPrefill } from "@/ui/organisms/DealWizard";
import type { TDealCopyPrefill } from "@/ui/organisms/DealWizard";
import { getNow } from "@/utils/dateService";
import { fetchAllPages } from "./wireDealDetail";

function mapDeal(d: TApiDeal) {
    return {
        id: d.id,
        companyId: d.company_id,
        name: d.name,
        code: d.code,
        serviceProviderId: d.service_provider_id,
        portfolioCompanyId: d.portfolio_company_id,
        financeUserIds: d.finance_user_ids ?? [],
        dealFileType: d.deal_file_type,
        coaMappingMode: d.coa_mapping_mode,
        glType: d.gl_type,
        importAvailable: d.import_available,
        finalFileDestination: d.final_file_destination,
        requiresSegment2: d.requires_segment_2,
        requiresSegment3: d.requires_segment_3,
        requiresSegment4: d.requires_segment_4,
        requiresSegment5: d.requires_segment_5,
        requiresSegment6: d.requires_segment_6,
        requiresSegment7: d.requires_segment_7,
        segment2MappingType: d.segment_2_mapping_type,
        segment4MappingType: d.segment_4_mapping_type,
        segment5MappingType: d.segment_5_mapping_type,
        segment6MappingType: d.segment_6_mapping_type,
        segment7MappingType: d.segment_7_mapping_type,
        monthlyDeadlineDay: d.monthly_deadline_day,
        quarterlyDeadlineDay: d.quarterly_deadline_day,
        yearEndDeadline: d.year_end_deadline,
        startPeriod: d.start_period,
        yardiEntityId: d.yardi_entity_id,
        yardiDatabase: d.yardi_database,
        acceptsOtb: d.accepts_otb,
        otbComparisonEnabled: d.otb_comparison_enabled,
        acceptsFinJournals: d.accepts_fin_journals,
        isActive: d.is_active,
        isTest: d.is_test,
        notes: d.notes,
        tbFilteringRules: [] as Array<{
            filteringColumnHeader: string;
            filteringValue: string[];
            includeFilteredValues: boolean;
        }>,
        glFilteringRules: [] as Array<{
            filteringColumnHeader: string;
            filteringValue: string[];
            includeFilteredValues: boolean;
        }>,
        // The list endpoint returns the summary shape, so both timestamps can be
        // absent even though TApiDeal types them as present.
        activatedAt: d.activated_at ?? null,
        deactivatedAt: d.deactivated_at ?? null,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
    };
}

export function wireDeals({ store, useAppStore, api }: TCompositionDeps) {
    const ViewDeals = createViewDeals({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const dealsState = useAppStore((state) => state.dealsListState);
            const currentUser = useAppStore((state) => state.user);
            const canCreateDeal = currentUser?.permissions.includes("deal_create") ?? false;
            const canEditDeal = currentUser?.permissions.includes("deal_edit") ?? false;
            const [serviceProviders, setServiceProviders] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                    coaMappingMode?: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });
            const [portfolioCompanies, setPortfolioCompanies] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                    coaMappingMode?: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });
            const [users, setUsers] = useState<{
                data: Array<{ id: string; display_name: string; email: string }> | null;
                loading: boolean;
            }>({ data: null, loading: true });
            const [filterOptions, setFilterOptions] = useState<{
                serviceProviders: Array<{ id: string; name: string }>;
                portfolioCompanies: Array<{ id: string; name: string }>;
            }>({ serviceProviders: [], portfolioCompanies: [] });

            // Track last query so onRefresh can replay it
            const lastQueryRef = useRef<Parameters<typeof api.deals.list>[0]>({});

            const loadDeals = useCallback(
                (query: Parameters<typeof api.deals.list>[0], signal?: AbortSignal) => {
                    lastQueryRef.current = query;
                    store.getState().setDealsLoading();
                    api.deals
                        .list(query, signal)
                        .then((response) => {
                            store.getState().setDealsList({
                                items: response.items.map(mapDeal),
                                total: response.total,
                                page: response.page,
                                pageSize: response.page_size,
                                totalUnfiltered: response.total_unfiltered ?? undefined,
                            });
                        })
                        .catch((error) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store.getState().setDealsError(error);
                        });
                },
                // Re-create when company changes so the server table query is re-issued.
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const onPageChange = useCallback((_page: number) => {
                // Page changes are managed by useServerTable inside the view
            }, []);

            const loadReferenceData = useCallback(() => {
                setServiceProviders({ data: null, loading: true });
                setPortfolioCompanies({ data: null, loading: true });
                setUsers({ data: null, loading: true });

                api.serviceProviders
                    .list({
                        is_active: true,
                        page_size: 1000,
                        business_partner_type: "service_provider",
                    })
                    .then((response) => {
                        setServiceProviders({
                            data: response.items.map((sp) => ({
                                id: sp.id,
                                name: sp.name,
                                code: sp.code,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setServiceProviders({ data: [], loading: false });
                    });

                api.portfolioCompanies
                    .list({
                        active_only: true,
                        skip: 0,
                        limit: 1000,
                    })
                    .then((response) => {
                        setPortfolioCompanies({
                            data: response.items.map((pc) => ({
                                id: pc.id,
                                name: pc.name,
                                code: pc.code,
                                coaMappingMode: pc.coa_mapping_mode,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setPortfolioCompanies({ data: [], loading: false });
                    });

                api.users
                    .list({ is_active: true, page_size: 1000, finance_team: true })
                    .then((response) => {
                        setUsers({
                            data: response.items.map((u) => ({
                                id: u.id,
                                display_name: u.display_name,
                                email: u.email,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setUsers({ data: [], loading: false });
                    });

                api.deals
                    .filterOptions()
                    .then((opts) => {
                        setFilterOptions({
                            serviceProviders: opts.serviceProviders.map((o) => ({
                                id: o.id,
                                name: o.name,
                            })),
                            portfolioCompanies: opts.portfolioCompanies.map((o) => ({
                                id: o.id,
                                name: o.name,
                            })),
                        });
                    })
                    .catch(() => {
                        // filterOptions failure is non-fatal; filters just won't have options
                    });

                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            // onMount only loads reference data; useServerTable auto-triggers loadDeals
            const onMount = useCallback(() => {
                loadReferenceData();
            }, [loadReferenceData]);

            const onRefresh = useCallback(() => {
                loadDeals(lastQueryRef.current);
            }, [loadDeals]);

            return {
                onMount,
                onUnmount: NOOP,
                useDealsState: () => dealsState,
                useServiceProviders: () => serviceProviders,
                usePortfolioCompanies: () => portfolioCompanies,
                useUsers: () => users,
                onDealClick: useCallback((_deal: { id: string }) => {
                    // TODO: navigate to deal detail page
                }, []),
                onCreateDeal: useCallback(async (data: Parameters<typeof api.deals.create>[0]) => {
                    await api.deals.create(data);
                }, []),
                onExtractColumns: useCallback(async (file: File, headerRowNumber: number) => {
                    return api.deals.extractColumns(file, headerRowNumber);
                }, []),
                onValidateSegmentMappingFile: useCallback(
                    async (segmentNumber: number, file: File) => {
                        return api.deals.validateSegmentMappingFile(segmentNumber, file);
                    },
                    [],
                ),
                onValidatePropcodeMappingFile: useCallback(async (file: File) => {
                    return api.deals.validatePropcodeMappingFile(file);
                }, []),
                onPageChange,
                onRefresh,
                canCreateDeal,
                loadDeals,
                useFilterOptions: () => filterOptions,
                canEditDeal,
                onToggleDealActive: useCallback(async (deal: TDeal) => {
                    await api.deals.update(deal.id, { is_active: !deal.isActive });
                }, []),
            };
        },
    });

    const ViewDealCreate = createViewDealCreate({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const navigate = useNavigate();

            const [preselectedServiceProviderId, setPreselectedServiceProviderId] = useState<
                string | null
            >(null);

            const [serviceProviders, setServiceProviders] = useState<{
                data: Awaited<ReturnType<typeof api.serviceProviders.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [portfolioCompanies, setPortfolioCompanies] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                    coaMappingMode?: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [users, setUsers] = useState<{
                data: Awaited<ReturnType<typeof api.users.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [copySource, setCopySource] = useState<TAsyncState<TDealCopyPrefill>>({
                status: "idle",
                data: null,
                error: null,
            });

            // Read everything the wizard needs from the deal being copied. Nothing is
            // written: the copy is only created when the wizard is submitted.
            const loadCopySource = useCallback(async (dealId: string) => {
                setCopySource({ status: "pending", data: null, error: null });
                try {
                    const [deal, columnMappings, segmentMappings, propcodeMappings, otbInfo] =
                        await Promise.all([
                            api.deals.get(dealId),
                            fetchAllPages((skip, limit) =>
                                api.deals.getColumnMappings(dealId, { skip, limit }),
                            ),
                            fetchAllPages((skip, limit) =>
                                api.deals.getSegmentMappings(dealId, { skip, limit }),
                            ),
                            fetchAllPages((skip, limit) =>
                                api.deals.getPropcodeMappings(dealId, { skip, limit }),
                            ),
                            api.deals.getOTBInfo(dealId).catch(() => null),
                        ]);

                    let otbFile: File | null = null;
                    let otbUnavailableReason: string | null = null;
                    if (otbInfo?.has_otb && otbInfo.file_id) {
                        try {
                            otbFile = await api.files.fetchOriginal(otbInfo.file_id);
                        } catch {
                            otbUnavailableReason =
                                "The OTB file of the copied deal could not be loaded. Upload an OTB file or use a placeholder.";
                        }
                    } else if (otbInfo === null) {
                        otbUnavailableReason =
                            "The OTB of the copied deal could not be loaded. Upload an OTB file or use a placeholder.";
                    }

                    setCopySource({
                        status: "fulfilled",
                        data: buildDealCopyPrefill(
                            {
                                deal,
                                columnMappings,
                                segmentMappings,
                                propcodeMappings,
                                otbFile,
                                otbUnavailableReason,
                            },
                            getNow(),
                        ),
                        error: null,
                    });
                } catch (err) {
                    setCopySource({
                        status: "rejected",
                        data: null,
                        error:
                            err instanceof Error
                                ? err
                                : new Error("Failed to load the deal to copy"),
                    });
                }
            }, []);

            const loadAll = useCallback(
                async (bpId?: string) => {
                    // Store preselected service provider ID
                    if (bpId) {
                        setPreselectedServiceProviderId(bpId);
                    }

                    setServiceProviders({ data: null, loading: true });
                    try {
                        const spData = await api.serviceProviders.list({
                            skip: 0,
                            limit: 1000,
                            business_partner_type: "service_provider",
                        });
                        setServiceProviders({ data: spData.items, loading: false });
                    } catch {
                        setServiceProviders({ data: [], loading: false });
                    }

                    setPortfolioCompanies({ data: null, loading: true });
                    try {
                        const pcData = await api.portfolioCompanies.list({
                            skip: 0,
                            limit: 100,
                        });
                        setPortfolioCompanies({
                            data: pcData.items.map((pc) => ({
                                id: pc.id,
                                name: pc.name,
                                code: pc.code,
                                coaMappingMode: pc.coa_mapping_mode,
                            })),
                            loading: false,
                        });
                    } catch {
                        setPortfolioCompanies({ data: [], loading: false });
                    }

                    setUsers({ data: null, loading: true });
                    try {
                        const userData = await api.users.list({
                            page_size: 1000,
                            finance_team: true,
                        });
                        setUsers({ data: userData.items, loading: false });
                    } catch {
                        setUsers({ data: [], loading: false });
                    }
                },
                [effectiveCompanyId],
            );

            return {
                onMount: loadAll,
                onUnmount: NOOP,
                useServiceProviders: () => serviceProviders,
                usePortfolioCompanies: () => portfolioCompanies,
                useUsers: () => users,
                usePreselectedServiceProviderId: () => preselectedServiceProviderId,
                onCreateDeal: useCallback(async (data: Parameters<typeof api.deals.create>[0]) => {
                    const createdDeal = await api.deals.create(data);
                    return createdDeal;
                }, []),
                onCheckDealNameTaken: useCallback(
                    async (
                        name: string,
                        partner: { serviceProviderId?: string; portfolioCompanyId?: string },
                    ) => {
                        const wanted = name.trim().toLowerCase();
                        if (!wanted) return false;
                        // `name` is a contains-filter and the list includes inactive deals,
                        // which also hold their name; compare exactly, ignoring case, as the
                        // unique index does.
                        const page = await api.deals.list({
                            name: name.trim(),
                            service_provider_id: partner.serviceProviderId,
                            portfolio_company_id: partner.portfolioCompanyId,
                            limit: 100,
                        });
                        return page.items.some((d) => d.name.trim().toLowerCase() === wanted);
                    },
                    [],
                ),
                useCopySource: () => copySource,
                onLoadCopySource: loadCopySource,
                onDealCreateSuccess: useCallback(
                    (createdDeal?: TApiDeal, partnerId?: string) => {
                        // A copy lands on the new deal so the carried-over setup can be reviewed.
                        if (createdDeal && partnerId) {
                            navigate(
                                routerPaths.admin.businessPartnerDealDetail(
                                    partnerId,
                                    createdDeal.id,
                                ),
                            );
                            return;
                        }
                        navigate(routerPaths.admin.businessPartners);
                    },
                    [navigate],
                ),
                onExtractColumns: useCallback(async (file: File, headerRowNumber: number) => {
                    return api.deals.extractColumns(file, headerRowNumber);
                }, []),
                onValidateSegmentMappingFile: useCallback(
                    async (segmentNumber: number, file: File) => {
                        return api.deals.validateSegmentMappingFile(segmentNumber, file);
                    },
                    [],
                ),
                onValidatePropcodeMappingFile: useCallback(async (file: File) => {
                    return api.deals.validatePropcodeMappingFile(file);
                }, []),
                onValidateOTB: useCallback(
                    async (
                        file: File,
                        columnMappings: Array<{
                            file_type: string;
                            field_type: string;
                            column_name: string;
                            header_row_number: number;
                        }>,
                        startPeriod: string,
                        filteringRules?: Array<{
                            filtering_column_header: string;
                            filtering_value: string[];
                            include_filtered_values: boolean;
                        }>,
                    ) => {
                        return api.deals.validateOTB(
                            file,
                            columnMappings,
                            startPeriod,
                            filteringRules,
                        );
                    },
                    [],
                ),
                onUploadOTB: useCallback(
                    async (
                        dealId: string,
                        file: File,
                        options?: { sheetName?: string; dateFormat?: string },
                    ) => {
                        return api.deals.uploadOTB(dealId, file, options);
                    },
                    [],
                ),
                onGeneratePlaceholderOTB: useCallback(
                    async (dealId: string, propertyCode: string) => {
                        return api.deals.generatePlaceholderOTB(dealId, propertyCode);
                    },
                    [],
                ),
                onCancel: useCallback(
                    (_bpId?: string) => {
                        navigate(routerPaths.admin.businessPartners);
                    },
                    [navigate],
                ),
            };
        },
    });

    return { ViewDeals, ViewDealCreate };
}
